// Real-time feedback #1 (Part B): optimistic in-payload mutation for
// lifecycle/priority feedback kinds.
//
// Mirror of app.ts::applyKilEventToPayload, extracted into its own
// module so it's unit-testable (app.ts has top-level bootstrap side
// effects that run on import). Used in two places by renderRun:
//   1. in-session, on a `feedback-state-changed` CustomEvent, and
//   2. at load, replaying the recipient's fetched events onto the
//      payload (closes/priority don't touch the published blob until
//      the next cron, so this is what makes the dim/move survive a
//      reload / navigation).
//
// Each branch mirrors the agent absorber (agent/feedback_absorb.py) so
// the optimistic state matches what the next cron will render — a
// divergence here would "revert" after absorb.

/** The subset of the v4 briefing payload this applier reads/writes.
 *  Structurally compatible with the renderer's payload shape. */
export interface OptimisticPayload {
  threads?: Array<{
    slug?: string;
    status?: string;
    // #37: phase band. category_move_to_phase mutates it in-session so the
    // render re-bands the Category before the next cron stamps it from state.
    phase?: string | null;
    // #38: sub-category grouping overlay. Pending add/rename/close/reopen
    // events mutate this list in-session so pickers + the render partition
    // see groups before the next cron stamps them from state.
    subcategories?: Array<{
      slug?: string;
      name?: string;
      status?: string;
      created_at?: string | null;
      closed_at?: string | null;
      closed_reason?: string | null;
      seq?: number | null;
    }>;
    work_items?: Array<{
      slug?: string;
      status?: string;
      // #38: grouping pointer (null = ungrouped). item_move_to_subcategory
      // sets it; a reopen onto a closed group NULLs it (policy 3).
      subcategory_slug?: string | null;
      closed_reason?: string | null;
      closure_candidate?: unknown;
      action_items?: Array<{
        ai_slug?: string;
        status?: string;
        closed_reason?: string | null;
        closure_candidate?: unknown;
        priority_status?: string;
        reason?: string | null;
        impact?: string | null;
        off_hold_condition?: string | null;
        // Client 2026-08-12: the rendered question list. A dismissal drops its
        // question from here on submit so the row disappears immediately —
        // otherwise the recipient clicks "Dismiss question", nothing visibly
        // happens, and the clutter he asked us to remove is still on screen
        // until the ~10-minute mid-week refresh.
        open_questions_v4?: Array<{ qid?: string; text?: string }>;
      }>;
    }>;
  }>;
}

/** The lifecycle/priority kinds this applier handles. forms.ts gates the
 *  `feedback-state-changed` dispatch on the same set. */
export const OPTIMISTIC_STATE_KINDS: readonly string[] = [
  "action_close",
  "action_reopen",
  "work_item_close",
  "work_item_reopen",
  // C5 (Ray 2026-07-21): the disposition verbs echo in-session too, so an
  // Action acknowledged / marked not-relevant grays out immediately AND the
  // parent Item's close gate (openActionCount) recomputes — un-graying "Close
  // Item" once no OPEN Action remains. Previously only close/reopen echoed, so
  // acknowledging an Action's siblings left the Item stuck un-closable in-session.
  "work_item_acknowledge",
  "action_acknowledge",
  "work_item_not_relevant",
  "action_not_relevant",
  "thread_close",
  "thread_reopen",
  "action_dismiss_closure_candidate",
  // Option Y (2026-06-03): dismiss an Item-level closure proposal — the
  // "(Agent proposes closing)" suffix clears optimistically.
  "work_item_dismiss_closure_candidate",
  "action_priority_change",
  // A①: a merge CLOSES its sources (the absorber does the same), so they
  // gray into Recently Closed on submit — fixes Ray's "by the 4th merge
  // I'm lost, nothing happened so I can't tell which ones I folded."
  "work_item_merge",
  "action_merge",
  // #38: sub-category grouping echoes in-session so the recipient sees the
  // regroup pre-cron AND the move picker / "≥1 open group" gate see pending
  // adds without a reload (the pending-aware picker mechanism).
  "subcategory_add",
  "subcategory_rename",
  "subcategory_close",
  "subcategory_reopen",
  // #74b: delete echoes in-session — the group vanishes + its Items re-render
  // un-grouped without waiting for the next cron.
  "subcategory_delete",
  "item_move_to_subcategory",
  // #37: re-banding echoes in-session — the Category jumps to its new phase
  // band on submit without waiting for the next cron's stamp.
  "category_move_to_phase",
  // #76: promote-to-Item echoes by closing the source Action in-session (the
  // new Item materializes on the next cron).
  "action_to_item",
  // Client 2026-08-12: dismissing a question hides it in-session. The whole
  // point of the affordance is removing visual clutter, so a dismissal that
  // leaves the question on screen has done nothing the recipient can see.
  "action_open_question_dismiss",
];

/** Coerce a merge payload's source-slug field, which may be a JSON array or
 *  a single string (the absorb-gap shape), into a string[]. */
function toSlugList(v: unknown): string[] {
  if (Array.isArray(v)) return v.map((s) => String(s)).filter(Boolean);
  if (typeof v === "string" && v.trim()) return [v.trim()];
  return [];
}

/** #38: today's date as ISO `YYYY-MM-DD` — the optimistic stand-in for the
 *  agent stamp's created_at / closed_at. State writes the real recorded date
 *  at absorb and the next cron re-projects it, so this only governs the
 *  pre-cron in-session render. */
function todayISODate(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Apply one feedback event to the in-memory payload. Returns true if it
 *  mutated something (parse miss / unknown kind / target not found → false). */
export function applyFeedbackEventToPayload(
  payload: OptimisticPayload,
  kind: string,
  targetId: string,
  eventPayload: Record<string, unknown>,
): boolean {
  const threadMatch = targetId.match(/^thread:([^/]+)$/);
  const itemMatch = targetId.match(/^thread:([^/]+)\/work_item:([^/]+)$/);
  const actionMatch = targetId.match(
    /^thread:([^/]+)\/work_item:([^/]+)\/action_item:([^/]+)$/,
  );
  // #38: sub-category targets — `thread:<t>/subcategory:<sc>`.
  const subcategoryMatch = targetId.match(
    /^thread:([^/]+)\/subcategory:([^/]+)$/,
  );

  // Category (thread) close/reopen. NB: KIL never parses thread-level
  // targets, so this match is unique to this applier.
  if (kind === "thread_close" || kind === "thread_reopen") {
    if (!threadMatch) return false;
    const [, threadSlug] = threadMatch;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      t.status = kind === "thread_close" ? "closed" : "open";
      return true;
    }
    return false;
  }

  // #37: category_move_to_phase targets the Category (`thread:<t>`); set its
  // phase band so the render re-bands it this session. Pure attribute move —
  // display_number untouched, no relocation. Mirrors apply_v4_category_move_to_phase.
  if (kind === "category_move_to_phase") {
    if (!threadMatch) return false;
    const [, threadSlug] = threadMatch;
    const toPhase = typeof eventPayload.to_phase === "string"
      ? eventPayload.to_phase : "";
    if (!toPhase) return false;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      t.phase = toPhase;
      return true;
    }
    return false;
  }

  if (
    kind === "work_item_close" || kind === "work_item_reopen"
    || kind === "work_item_dismiss_closure_candidate"
  ) {
    if (!itemMatch) return false;
    const [, threadSlug, wiSlug] = itemMatch;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      for (const wi of t.work_items ?? []) {
        if (wi.slug !== wiSlug) continue;
        if (kind === "work_item_close") {
          wi.status = "closed";
          // Option Y: closing clears the Item-level proposal (mirror the
          // absorber close_work_item NULLing closure_candidate_*).
          wi.closure_candidate = null;
        } else if (kind === "work_item_reopen") {
          wi.status = "open";
          // #38 policy 3 (binding amendment): a reopened Item whose group is
          // closed (or gone) returns UNGROUPED. The stamp's orphan sweep does
          // this every cron; mirror it here so the open Item never renders
          // under a closed group's header-only tombstone (open-work-hidden is
          // the worst failure class this system guards against).
          if (wi.subcategory_slug) {
            const g = (t.subcategories ?? []).find(
              (s) => s.slug === wi.subcategory_slug,
            );
            if (!g || g.status !== "open") wi.subcategory_slug = null;
          }
        } else {
          // work_item_dismiss_closure_candidate — park the Item open;
          // drop the proposal (mirror apply_v4_work_item_dismiss_*).
          wi.closure_candidate = null;
        }
        return true;
      }
    }
    return false;
  }

  if (kind === "action_open_question_dismiss") {
    // Drop the dismissed question from the rendered list so it disappears on
    // submit. Keyed on `qid` — the per-question ✎ always injects one, and it is
    // the identity that survives the synth re-wording the question between runs
    // (#52b); matching on text here would silently miss after any reword.
    if (!actionMatch) return false;
    const qid = eventPayload.qid;
    if (typeof qid !== "string" || !qid) return false;
    const [, threadSlug, wiSlug, aiSlug] = actionMatch;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      for (const wi of t.work_items ?? []) {
        if (wi.slug !== wiSlug) continue;
        for (const ai of wi.action_items ?? []) {
          if (ai.ai_slug !== aiSlug) continue;
          const before = ai.open_questions_v4 ?? [];
          const after = before.filter((q) => q.qid !== qid);
          if (after.length === before.length) return false;
          ai.open_questions_v4 = after;
          return true;
        }
      }
    }
    return false;
  }

  if (
    kind === "action_close" || kind === "action_reopen"
    || kind === "action_dismiss_closure_candidate"
    || kind === "action_priority_change"
  ) {
    if (!actionMatch) return false;
    const [, threadSlug, wiSlug, aiSlug] = actionMatch;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      for (const wi of t.work_items ?? []) {
        if (wi.slug !== wiSlug) continue;
        for (const ai of wi.action_items ?? []) {
          if (ai.ai_slug !== aiSlug) continue;
          if (kind === "action_close") {
            ai.status = "closed";
            // Absorber clears closure_candidate on close
            // (feedback_absorb.py): a proposed-then-closed Action
            // shouldn't keep the "(Agent proposes closing)" suffix.
            ai.closure_candidate = null;
          } else if (kind === "action_reopen") {
            ai.status = "open";
          } else if (kind === "action_dismiss_closure_candidate") {
            ai.closure_candidate = null;
          } else {
            // action_priority_change — mirror feedback_absorb.py:333.
            // Form key is `new_priority` (NOT priority_status). Clear
            // reason/impact/off_hold to null when absent (Active/On-Hold
            // hide them) so no stale metadata clings to the new pill.
            // `due` is intentionally NOT touched — the absorber drops it
            // for a priority change, so showing it would revert next cron.
            const np = eventPayload.new_priority;
            if (typeof np === "string") ai.priority_status = np;
            ai.reason =
              typeof eventPayload.reason === "string" ? eventPayload.reason : null;
            ai.impact =
              typeof eventPayload.impact === "string" ? eventPayload.impact : null;
            ai.off_hold_condition =
              typeof eventPayload.off_hold_condition === "string"
                ? eventPayload.off_hold_condition
                : null;
          }
          return true;
        }
      }
    }
    return false;
  }

  // C5 (2026-07-21): work_item_acknowledge / work_item_not_relevant — set the
  // Item's terminal disposition status so it grays in-session (mirror of the
  // closed treatment; the next cron then DROPS it via enforce_state_acknowledged
  // / _not_relevant). Clears any "(Agent proposes closing)" proposal too.
  if (kind === "work_item_acknowledge" || kind === "work_item_not_relevant") {
    if (!itemMatch) return false;
    const [, threadSlug, wiSlug] = itemMatch;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      for (const wi of t.work_items ?? []) {
        if (wi.slug !== wiSlug) continue;
        wi.status = kind === "work_item_acknowledge" ? "acknowledged" : "not_relevant";
        wi.closure_candidate = null;
        return true;
      }
    }
    return false;
  }

  // C5 (2026-07-21): action_acknowledge / action_not_relevant — set the Action's
  // terminal disposition status. Beyond the in-session gray, this is what lets
  // the parent Item's close gate (openActionCount in briefings.ts) recompute so
  // "Close Item" un-grays once every child Action is resolved. Mirrors the
  // Worker's effectiveOpenActionsUnderItem, which now also treats these as
  // resolving the Action.
  if (kind === "action_acknowledge" || kind === "action_not_relevant") {
    if (!actionMatch) return false;
    const [, threadSlug, wiSlug, aiSlug] = actionMatch;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      for (const wi of t.work_items ?? []) {
        if (wi.slug !== wiSlug) continue;
        for (const ai of wi.action_items ?? []) {
          if (ai.ai_slug !== aiSlug) continue;
          ai.status = kind === "action_acknowledge" ? "acknowledged" : "not_relevant";
          ai.closure_candidate = null;
          return true;
        }
      }
    }
    return false;
  }

  // --- #38 sub-category grouping --------------------------------------------
  // subcategory_add targets the parent Category (`thread:<t>`): append the
  // group (or idempotently refresh its name if the slug already exists, the
  // absorber's already-exists branch) so pickers + the render partition see it
  // this session. Mirrors apply_v4_subcategory_add.
  if (kind === "subcategory_add") {
    if (!threadMatch) return false;
    const [, threadSlug] = threadMatch;
    const slug = typeof eventPayload.subcategory_slug === "string"
      ? eventPayload.subcategory_slug : "";
    const name = typeof eventPayload.name === "string" ? eventPayload.name : "";
    if (!slug) return false;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      const groups = (t.subcategories ??= []);
      const existing = groups.find((g) => g.slug === slug);
      if (existing) {
        if (name) existing.name = name;
      } else {
        const maxSeq = groups.reduce(
          (m, g) => Math.max(m, typeof g.seq === "number" ? g.seq : 0),
          0,
        );
        groups.push({
          slug, name, status: "open",
          created_at: todayISODate(), seq: maxSeq + 1,
        });
      }
      return true;
    }
    return false;
  }

  // item_move_to_subcategory targets the Item (`thread:<t>/work_item:<wi>`);
  // set its grouping pointer. `to_subcategory_slug` null/empty = ungroup.
  if (kind === "item_move_to_subcategory") {
    if (!itemMatch) return false;
    const [, threadSlug, wiSlug] = itemMatch;
    const to = eventPayload.to_subcategory_slug;
    const ptr = typeof to === "string" && to ? to : null;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      for (const wi of t.work_items ?? []) {
        if (wi.slug !== wiSlug) continue;
        wi.subcategory_slug = ptr;
        return true;
      }
    }
    return false;
  }

  // subcategory_rename / _close / _reopen target the group
  // (`thread:<t>/subcategory:<sc>`); mutate the matching subcategories[] entry.
  if (
    kind === "subcategory_rename" || kind === "subcategory_close"
    || kind === "subcategory_reopen"
  ) {
    if (!subcategoryMatch) return false;
    const [, threadSlug, scSlug] = subcategoryMatch;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      for (const g of t.subcategories ?? []) {
        if (g.slug !== scSlug) continue;
        if (kind === "subcategory_rename") {
          const to = eventPayload.to_name;
          if (typeof to === "string" && to.trim()) g.name = to.trim();
        } else if (kind === "subcategory_close") {
          g.status = "closed";
          g.closed_at = todayISODate();
          g.closed_reason = typeof eventPayload.reason === "string"
            ? eventPayload.reason : null;
        } else {
          g.status = "open";
          g.closed_at = null;
          g.closed_reason = null;
        }
        return true;
      }
    }
    return false;
  }

  // #74b: subcategory_delete targets the group (`thread:<t>/subcategory:<sc>`).
  // UNLIKE close (which tombstones the row), delete REMOVES the group from
  // subcategories[] AND ungroups its member Items (subcategory_slug → null, any
  // status) so the render partition shows them flat. Mirrors
  // apply_v4_subcategory_delete (drop row + null members).
  if (kind === "subcategory_delete") {
    if (!subcategoryMatch) return false;
    const [, threadSlug, scSlug] = subcategoryMatch;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      const groups = t.subcategories;
      if (!groups) return false;
      const idx = groups.findIndex((g) => g.slug === scSlug);
      if (idx === -1) return false;
      groups.splice(idx, 1);
      for (const wi of t.work_items ?? []) {
        if (wi.subcategory_slug === scSlug) wi.subcategory_slug = null;
      }
      return true;
    }
    return false;
  }

  // #76: action_to_item — optimistically CLOSE the source Action with the
  // "Promoted to Item: X" breadcrumb (it moves to Recently Closed under its old
  // Item). The NEW Item is created server-side + materializes on the next cron
  // (cross-Category creation isn't echoed in-session, same as item_move_to_
  // category / place_from_unfiled) — the close is the clear in-session signal.
  if (kind === "action_to_item") {
    if (!actionMatch) return false;
    const [, threadSlug, wiSlug, aiSlug] = actionMatch;
    const label = typeof eventPayload.name === "string" ? eventPayload.name : "";
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      for (const wi of t.work_items ?? []) {
        if (wi.slug !== wiSlug) continue;
        for (const ai of wi.action_items ?? []) {
          if (ai.ai_slug !== aiSlug) continue;
          ai.status = "closed";
          ai.closed_reason = label ? `Promoted to Item: ${label}` : "Promoted to Item";
          ai.closure_candidate = null;
          return true;
        }
      }
    }
    return false;
  }

  // A①: work_item_merge — the picked source Items fold into the target and
  // CLOSE (feedback_absorb.py + state.apply_work_item_merge), but their
  // context carries forward into the target, so they're "merged", not just
  // "closed". Mark status=closed + a merge marker so they route to the
  // "Recently Merged → into X" section. `target_label` rides the event
  // (threaded by forms.ts) so the label survives reload + names the target.
  if (kind === "work_item_merge") {
    const sources = toSlugList(eventPayload.source_work_item_slugs);
    if (!sources.length) return false;
    const label = typeof eventPayload.target_label === "string"
      ? eventPayload.target_label : "";
    let changed = false;
    for (const t of payload.threads ?? []) {
      for (const wi of t.work_items ?? []) {
        if (wi.slug && sources.includes(wi.slug)) {
          wi.status = "closed";
          wi.closed_reason = label ? `merged into ${label}` : "merged";
          changed = true;
        }
      }
    }
    return changed;
  }

  // A①: action_merge — the picked source Actions close into the target; same
  // "merged" treatment. B8: the picker now spans Items, so a source may live
  // under its ORIGIN Item until next run's re-parent applies — gray it there
  // too. `merge_origin_work_item_slugs` (threaded by forms.ts) scopes the
  // search to the target Item + the picked sources' origins, so a coincidental
  // same-ai_slug under an unrelated Item is never touched. wi slugs are
  // project-unique, so searching across threads by slug is safe.
  if (kind === "action_merge") {
    if (!actionMatch) return false;
    const [, , wiSlug] = actionMatch;
    const sources = toSlugList(eventPayload.source_action_item_slugs);
    if (!sources.length) return false;
    const label = typeof eventPayload.target_label === "string"
      ? eventPayload.target_label : "";
    const searchWis = new Set<string>([
      wiSlug ?? "",
      ...toSlugList(eventPayload.merge_origin_work_item_slugs),
    ]);
    let changed = false;
    for (const t of payload.threads ?? []) {
      for (const wi of t.work_items ?? []) {
        if (!wi.slug || !searchWis.has(wi.slug)) continue;
        for (const ai of wi.action_items ?? []) {
          if (ai.ai_slug && sources.includes(ai.ai_slug)) {
            ai.status = "closed";
            ai.closed_reason = label ? `merged into ${label}` : "merged";
            changed = true;
          }
        }
      }
    }
    return changed;
  }
  return false;
}
